import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { SvgIconComponent } from "@mui/icons-material";
import { ReactNode } from "react";

type PageHeaderProps = {
  title: string;
  description?: string;
  icon: SvgIconComponent;
  actions?: ReactNode;
};

const PageHeader = ({
  title,
  description,
  icon: Icon,
  actions,
}: PageHeaderProps) => {
  const theme = useTheme();

  return (
    <Paper
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        p: { xs: 2, sm: 2.5 },
        mb: 2.5,
        borderRadius: "20px",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(99,91,255,.12), rgba(18,25,43,.92) 52%)"
            : "linear-gradient(135deg, rgba(99,91,255,.10), rgba(255,255,255,.96) 52%)",
        "&::after": {
          content: '""',
          position: "absolute",
          width: 170,
          height: 170,
          top: -115,
          right: -55,
          borderRadius: "50%",
          border: "30px solid rgba(99,91,255,.07)",
          pointerEvents: "none",
        },
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        position="relative"
        zIndex={1}
      >
        <Stack direction="row" alignItems="center" spacing={1.75}>
          <Box
            sx={{
              width: 48,
              height: 48,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              borderRadius: "15px",
              color: "accentText",
              bgcolor: "rgba(99,91,255,.11)",
              border: "1px solid rgba(99,91,255,.13)",
            }}
          >
            <Icon />
          </Box>
          <Box>
            <Typography
              variant="h4"
              sx={{ fontSize: { xs: 24, sm: 30 }, lineHeight: 1.15 }}
            >
              {title}
            </Typography>
            {description && (
              <Typography
                color="text.secondary"
                sx={{ mt: 0.5, fontSize: { xs: 13, sm: 14 } }}
              >
                {description}
              </Typography>
            )}
          </Box>
        </Stack>
        {actions && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "stretch", sm: "flex-end" },
              gap: 1,
              flexWrap: "wrap",
              "& > .MuiButton-root": { flex: { xs: 1, sm: "initial" } },
            }}
          >
            {actions}
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default PageHeader;
